import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { formatePriceFn, formatPrice } from "../../../helper/commonFunction";
import React from "react";

export const normalizeFacetValue = (value) => {
  if (!value) return "Unknown";
  if (value === "bnb") {
    return value.toString().replace(/_/g, " ").toUpperCase().trim();
  }
  return value.toString().replace(/_/g, " ").trim();
};

const getFacetArr = (hotels = [], key) => {
  const facets = {};
  hotels?.forEach?.((h) => {
    const name =
      key === "propertyType" ? normalizeFacetValue(h?.[key]) : h?.[key];
    if (facets[name]) {
      facets[name].count += 1;
    } else {
      facets[name] = { name, count: 1 };
    }
  });
  return Object.values(facets);
};

const isValidHotel = (hotel) =>
  hotel?.amount !== undefined &&
  hotel?.amount !== null &&
  hotel.amount > 0 &&
  hotel?.propertyType &&
  hotel?.rating !== undefined &&
  hotel.rating > 0 &&
  !hotel?.loadingPrice;

// Fixed price thresholds — same buckets for all currencies (MMT-style).
// Only the currency label in the name changes; the numbers never shift as data loads.
const PRICE_THRESHOLDS = [0, 500, 1000, 2000, 4000, 7000];

// formatPrice always returns 2 decimal places; strip .00 for clean whole-number labels.
const fmtThreshold = (val) => {
  if (val === 0) return "0";
  const s = formatPrice(val, true, true);
  return s.endsWith(".00") ? s.slice(0, -3) : s;
};

const generatePriceRangeArr = (suffix, displayPrice) =>
  PRICE_THRESHOLDS.map((start, i) => {
    const isLast = i === PRICE_THRESHOLDS.length - 1;
    const end = isLast ? Infinity : PRICE_THRESHOLDS[i + 1];
    const fmtStart = fmtThreshold(start);
    return {
      start,
      end,
      name: isLast ? (
        <>{formatePriceFn(suffix, fmtStart, displayPrice)} +</>
      ) : (
        <>
          {formatePriceFn(suffix, fmtStart, displayPrice)} to{" "}
          {formatePriceFn(suffix, fmtThreshold(end), displayPrice)}
        </>
      ),
    };
  });

export const useFilterHotels = ({
  hotels,
  loading,
  selected,
  originalData,
}) => {
  const { agentActiveCurrency, displayPrice } = useSelector(
    (state) => state.userData,
  );
  const location = useLocation();
  const facetSet = useRef(false);

  // Memoized — only reparsed when the query string actually changes.
  const searchKeyword = useMemo(
    () =>
      new URLSearchParams(location.search)
        .get("keyword")
        ?.trim()
        .toLowerCase() ?? "",
    [location.search],
  );

  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [reset, setReset] = useState(1);
  const [priceRanges, setPriceRanges] = useState({ minPrice: 0, maxPrice: 0 });
  const [filterHideShow, setFilterHideShow] = useState({
    price: false,
    propertyType: false,
    ratings: false,
    priceSelection: false,
  });

  useEffect(() => {
    document.body.classList.toggle("filter-collapsed", isFilterCollapsed);
    return () => document.body.classList.remove("filter-collapsed");
  }, [isFilterCollapsed]);

  // Stable reference — uses functional updater so no deps needed.
  const filterHideShowFn = useCallback((menuItem) => {
    setFilterHideShow((prev) => ({
      ...prev,
      [menuItem]: !prev[menuItem],
    }));
  }, []);

  // Stable filter fn — only rebuilds when selected or searchKeyword changes.
  // Captures `selected` from closure directly (no need to pass as param).
  const applyFiltersForFacet = useCallback(
    (data = [], ignoreKey) => {
      let filtered = data;
      const sel = selected ?? {};

      if (ignoreKey !== "propertyType") {
        const selectedTypes = Object.keys(sel.propertyType ?? {}).filter(
          (k) => sel.propertyType[k]?.selected,
        );
        if (selectedTypes.length) {
          filtered = filtered.filter((h) =>
            selectedTypes.includes(normalizeFacetValue(h?.propertyType)),
          );
        }
      }

      if (searchKeyword) {
        const exact = [],
          partial = [],
          rest = [];
        filtered.forEach((h) => {
          const name = h?.name?.toLowerCase() ?? "";
          if (name === searchKeyword) exact.push(h);
          else if (name.includes(searchKeyword)) partial.push(h);
          else rest.push(h);
        });
        filtered = [...exact, ...partial, ...rest];
      }

      if (sel.hotelName?.length > 0) {
        const s = sel.hotelName.toLowerCase();
        const exact = [],
          partial = [];
        filtered.forEach((h) => {
          const name = h?.name?.toLowerCase() ?? "";
          if (name === s) exact.push(h);
          else if (name.includes(s)) partial.push(h);
        });
        filtered = [...exact, ...partial];
      }

      if (ignoreKey !== "ratings") {
        const selectedRatings = Object.keys(sel.ratings ?? {}).filter(
          (k) => sel.ratings[k]?.selected,
        );
        if (selectedRatings.length) {
          filtered = filtered.filter((h) =>
            selectedRatings.includes(String(h?.rating)),
          );
        }
      }

      if (ignoreKey !== "priceSelection") {
        const selectedPrices = Object.keys(sel.priceSelection ?? {}).filter(
          (k) => sel.priceSelection[k]?.selected,
        );
        if (selectedPrices.length) {
          filtered = filtered.filter((h) => {
            const amount = Number(h?.amount);
            return selectedPrices.some((k) => {
              const range = sel.priceSelection[k]?.d;
              return amount >= range?.start && amount <= range?.end;
            });
          });
        }
      }

      if (sel.minPrice && sel.maxPrice) {
        filtered = filtered.filter((h) => {
          const amount = Number(h?.amount);
          return amount >= sel.minPrice && amount <= sel.maxPrice;
        });
      }

      return filtered;
    },
    [selected, searchKeyword],
  );

  const filteredHotelsForFacets = useMemo(
    () => (originalData ?? []).filter(isValidHotel),
    [originalData],
  );

  const hotelsForPropertyType = useMemo(
    () => applyFiltersForFacet(filteredHotelsForFacets, "propertyType"),
    [filteredHotelsForFacets, applyFiltersForFacet],
  );

  const hotelsForRatings = useMemo(
    () => applyFiltersForFacet(filteredHotelsForFacets, "ratings"),
    [filteredHotelsForFacets, applyFiltersForFacet],
  );

  const hotelsForPrice = useMemo(
    () => applyFiltersForFacet(filteredHotelsForFacets, "priceSelection"),
    [filteredHotelsForFacets, applyFiltersForFacet],
  );

  // selected.hotelName not needed in deps — hotelsForPropertyType already recomputes
  // when selected changes (via applyFiltersForFacet).
  const propertyTypes = useMemo(
    () => getFacetArr(hotelsForPropertyType, "propertyType"),
    [hotelsForPropertyType, reset],
  );

  const ratings = useMemo(
    () =>
      getFacetArr(hotelsForRatings, "rating")
        .filter((r) => r.name && !isNaN(r.name) && Number(r.name) > 0)
        .map((r) => ({ ...r, name: Number(r.name) }))
        .sort((a, b) => a.name - b.name),
    [hotelsForRatings, reset],
  );

  useEffect(() => {
    if (facetSet.current || loading) return;
    if (filteredHotelsForFacets.length > 0) {
      facetSet.current = true;
      let minPrice = Infinity;
      let maxPrice = 0;
      filteredHotelsForFacets.forEach((h) => {
        const amount = Number(h?.amount);
        if (!amount) return;
        minPrice = Math.min(minPrice, amount);
        maxPrice = Math.max(maxPrice, amount);
      });
      setPriceRanges({
        minPrice: minPrice === Infinity ? 0 : minPrice,
        maxPrice,
      });
    }
  }, [filteredHotelsForFacets, loading]);

  // Fixed base ranges — thresholds never change, only currency label re-renders on currency switch.
  const baseRanges = useMemo(
    () => generatePriceRangeArr(agentActiveCurrency, displayPrice),
    [agentActiveCurrency, displayPrice],
  );

  // Single-pass map: count + disabled in one iteration instead of two chained maps.
  const priceSelection = useMemo(
    () => {
      if (!filteredHotelsForFacets.length) return [];
      return baseRanges.map((range) => {
        const count =
          hotelsForPrice?.filter(
            (d) => d?.amount >= range.start && d?.amount <= range.end,
          )?.length ?? 0;
        return { ...range, count, disabled: count === 0 };
      });
    },
    [baseRanges, hotelsForPrice, filteredHotelsForFacets.length, reset],
  );

  useEffect(() => {
    if (loading) {
      facetSet.current = false;
      setFilterHideShow({
        price: false,
        propertyType: false,
        ratings: false,
        priceSelection: false,
      });
      setPriceRanges({ minPrice: 0, maxPrice: 0 });
    }
  }, [loading]);

  return {
    filterHideShow,
    setIsFilterCollapsed,
    filterHideShowFn,
    isFilterCollapsed,
    propertyTypes,
    ratings,
    priceRanges,
    priceSelection,
    setReset,
    reset,
  };
};